/**
 * Logs that close the conversation — no next follow-up should be scheduled.
 */
export function isNotInterestedLog(log) {
  if (!log || typeof log !== 'string') return false;
  return /not interested/i.test(log.trim());
}

export function isFutureProspectStage(stage) {
  return stage === 'Future Prospect';
}

export function isDemoScheduledStage(stage) {
  return stage === 'Demo Scheduled' || stage === 'Demo';
}

export function isUpcomingContactStage(stage) {
  return isFutureProspectStage(stage) || isDemoScheduledStage(stage);
}

/**
 * Whether the Follow Up modal should collect next date + agenda.
 * Terminal stages and "Not Interested" outcomes do not need a next follow-up.
 * Future Prospect and Demo Scheduled always require a next-contact / demo date.
 */
export function requiresNextFollowUpFields({ stage, log } = {}) {
  if (isUpcomingContactStage(stage)) return true;
  if (stage === 'Deal Closed' || stage === 'Deal Dropped') return false;
  if (isNotInterestedLog(log)) return false;
  return true;
}

/**
 * Outcomes that close the lead out instead of scheduling a next touch still need a
 * written reason, so the drop is auditable in Stage History & logs.
 */
export function requiresClosureNote({ stage, log } = {}) {
  if (requiresNextFollowUpFields({ stage, log })) return false;
  return stage !== 'Deal Closed';
}

export function closureNoteLabel({ stage, log } = {}) {
  if (stage === 'Deal Dropped') return 'Reason For Dropping';
  if (stage === 'Deal Closed') return 'Closing Note';
  if (isNotInterestedLog(log)) return 'Reason — Why Not Interested';
  return 'Closure Note';
}

export function closureNotePlaceholder({ stage, log } = {}) {
  if (stage === 'Deal Dropped') return 'Why is this deal being dropped...';
  if (stage === 'Deal Closed') return 'Anything to note about this closure...';
  if (isNotInterestedLog(log)) return 'Budget, location, timeline, competitor...';
  return 'Why is this lead being closed out...';
}

export function upcomingContactDateLabel(stage) {
  if (isFutureProspectStage(stage)) return 'Next Contact Date (as told by client)';
  if (isDemoScheduledStage(stage)) return 'Demo Date & Time';
  return 'Next Follow Up Date & Time';
}

export function upcomingContactDateRequiredToast(stage) {
  if (isFutureProspectStage(stage)) return 'Select next contact date (as told by client)';
  if (isDemoScheduledStage(stage)) return 'Select demo date & time';
  return 'Select Next Follow Up Date';
}

export function upcomingContactPastDateToast(stage) {
  if (isDemoScheduledStage(stage)) return 'Demo date must be today or later';
  return 'Next contact date must be today or later';
}

export function upcomingAgendaLabel(stage) {
  if (isFutureProspectStage(stage)) return 'Agenda / Note For Scheduled Contact';
  if (isDemoScheduledStage(stage)) return 'Demo Agenda / Notes';
  return 'Agenda For Next Follow Up';
}

export function upcomingAgendaPlaceholder(stage) {
  if (isFutureProspectStage(stage)) return 'Why later / what to discuss when contacting...';
  if (isDemoScheduledStage(stage)) return 'What will you cover in the demo...';
  return 'Enter agenda here...';
}

export function upcomingAgendaRequiredToast(stage) {
  if (isFutureProspectStage(stage)) return 'Enter agenda / note for the scheduled contact';
  if (isDemoScheduledStage(stage)) return 'Enter demo agenda / notes';
  return 'Enter Agenda';
}

export function upcomingContactHelperText(stage) {
  if (isFutureProspectStage(stage)) {
    return 'Lead stays as Future Prospect until you move it to another stage.';
  }
  if (isDemoScheduledStage(stage)) {
    return 'Lead stays as Demo until you complete it and move to Presentation or Site Visited.';
  }
  return '';
}

/**
 * Next contact / demo date must be today or later (local day).
 */
export function isValidFutureProspectContactDate(followUpDate) {
  if (!followUpDate) return false;
  const parsed = new Date(followUpDate);
  if (Number.isNaN(parsed.getTime())) return false;
  const dayStart = new Date(parsed);
  dayStart.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dayStart >= today;
}

export const isValidUpcomingContactDate = isValidFutureProspectContactDate;
